import { memo, useEffect, useMemo } from 'react';

import { WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/flattenWorkspaceAgentBoardTree';
import {
  type WorkspaceAgentBoardListQuerySpec,
  type WorkspaceAgentBoardListSnapshotHandler,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentBoardListQuery';
import { usePrivateBoardsList } from '~/components/v2/hooks/queries/usePrivateBoardsList';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';

export type PrivateWorkspaceAgentBoardListQueryProps = {
  enabled: boolean;
  onSnapshot: WorkspaceAgentBoardListSnapshotHandler;
  spec: WorkspaceAgentBoardListQuerySpec;
};

/** Mounts one infinite list for an expanded or prefetched tree node. The tree hook cannot call `usePrivateBoardsList` per id. */
export const PrivateWorkspaceAgentBoardListQuery = memo(
  ({ enabled, onSnapshot, spec }: PrivateWorkspaceAgentBoardListQueryProps) => {
    const query = usePrivateBoardsList(spec.params, { enabled });
    // Stable identity: a new array every observer render would fail `previous.boards === snapshot.boards`
    // and rebuild the visible tree on hover.
    const boards = useMemo(
      () => (query.data ? query.data.pages.flatMap((page) => page.data) : WORKSPACE_AGENT_EMPTY_BOARD_TREE_BOARDS),
      [query.data],
    );

    const boardIds = useMemo(() => boards.map((board) => board.id), [boards]);

    useFetchObjectsPermissions({
      objects: { boardIds },
    });

    useEffect(() => {
      onSnapshot(spec.nodeId, {
        boards,
        fetchNextPage: query.fetchNextPage,
        hasNextPage: !!query.hasNextPage,
        isError: query.isError,
        isFetched: query.isFetched,
        isFetching: query.isFetching,
        isFetchNextPageError: query.isFetchNextPageError,
        isFetchingNextPage: query.isFetchingNextPage,
        refetch: query.refetch,
      });
    }, [
      boards,
      onSnapshot,
      query.fetchNextPage,
      query.hasNextPage,
      query.isError,
      query.isFetched,
      query.isFetching,
      query.isFetchNextPageError,
      query.isFetchingNextPage,
      query.refetch,
      spec.nodeId,
    ]);

    return null;
  },
);

PrivateWorkspaceAgentBoardListQuery.displayName = 'PrivateWorkspaceAgentBoardListQuery';
